import { usePublishEvent } from '@/analytics/usePublishEvent';
import { SignInApiClient } from '@/api/sign-in';
import { IdentifierType } from '@/api/types';
import { LoginFlow } from '@/common/LoginFlow';
import { useHandleErrors } from '@/common/utils/useHandleErrors';
import { useAllowedSignInMethods } from '@/hooks/useAllowedSignInMethods';
import { useHandleSignInVerification } from '@/hooks/useHandleSignInVerification';
import { StorageKeys } from '@/storage';
import { usePartnerStore } from '@/store/partner';
import { useSignInStore } from '@/store/signInStore';
import { MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineHeader } from '../LineHeader';
import { AppleButton, AuthorizationResponse } from './AppleButton';
import {
  CredentialResponse,
  GoogleSignIn,
  GoogleTestButton,
} from './GoogleSignIn';
import styles from './social-signin.module.scss';
import { SocialProviders } from './SocialProviders';

type SocialSignInProps = {
  onSocialClick?: (e: MouseEvent) => void;
  selectedFlow?: LoginFlow;
  setIsLoading: (loading: boolean) => void;
  setSelectedFlow: (flow: LoginFlow) => void;
  isLoading?: boolean;
  setIsChecked: (checked: boolean) => void;
  isChecked?: boolean;
  withHeader?: boolean;
};

const testEnvs = ['local', 'integration'];

export const SocialSignIn = ({
  isLoading = false,
  setSelectedFlow,
  setIsLoading,
  selectedFlow = LoginFlow.EMAIL_PHONE,
  isChecked,
  withHeader,
}: SocialSignInProps) => {
  const publishEvent = usePublishEvent();
  const handleErrors = useHandleErrors();
  const handleVerification = useHandleSignInVerification();
  const { t } = useTranslation();

  const { isGoogleSignInAllowed, isAppleSignInAllowed } =
    useAllowedSignInMethods();
  const updateSession = useSignInStore((s) => s.updateSession);

  const showLegalCheckbox = usePartnerStore((s) => s.showLegalCheckbox);

  const disableSocialButtons = isLoading || (showLegalCheckbox && !isChecked);

  const googleTestToken = useMemo(() => {
    if (
      typeof window !== 'undefined' &&
      testEnvs.includes(process.env.ENVIRONMENT_NAME)
    ) {
      return localStorage.getItem(StorageKeys.GoogleIdToken);
    }
    return null;
  }, []);

  /* istanbul ignore next */
  const onResolveSocial = async (
    provider: string,
    idToken: string,
    nonce?: string,
    elementId?: string,
  ) => {
    try {
      setIsLoading(true);
      setSelectedFlow(nonce ? LoginFlow.APPLE : LoginFlow.GOOGLE);

      const signInResponse = await SignInApiClient.signInWithSocial(
        provider,
        idToken,
        nonce,
      );

      updateSession({
        identifier: signInResponse.email,
        identifierType: IdentifierType.EMAIL,
        requiredActions: [],
      });

      await handleVerification(
        signInResponse,
        elementId ?? `${provider}-continue-button`,
        {
          extraData: {
            acr: `ep:${provider}`,
          },
        },
      );
    } catch (error) {
      handleErrors(error);
    }
  };

  /* istanbul ignore next */
  const handleGoogleResponse = async (
    response: CredentialResponse,
    elementId?: string,
  ) => {
    publishEvent('GoogleResponseSuccessful', 'google-login');
    await onResolveSocial(
      SocialProviders.GOOGLE.toLowerCase(),
      response.credential || '',
      undefined,
      elementId,
    );
  };

  /* istanbul ignore next */
  const handleAppleResponse = async (
    response: AuthorizationResponse,
    nonce: string,
  ) => {
    publishEvent('AppleResponseSuccessful', 'apple-login');
    await onResolveSocial(
      SocialProviders.APPLE.toLowerCase(),
      response.authorization.id_token || '',
      nonce,
    );
  };

  return (
    <>
      {withHeader && <LineHeader text={t('social header')} />}
      <div className={styles.buttons_container} data-testid="social-sign-in">
        {isGoogleSignInAllowed &&
          (googleTestToken ? (
            <GoogleTestButton
              onClick={() =>
                onResolveSocial(
                  SocialProviders.GOOGLE.toLowerCase(),
                  googleTestToken,
                )
              }
            />
          ) : (
            <GoogleSignIn
              disabled={disableSocialButtons}
              loading={
                isLoading && selectedFlow && selectedFlow === LoginFlow.GOOGLE
              }
              onCredentialResponse={handleGoogleResponse}
            />
          ))}
        {isAppleSignInAllowed && (
          <AppleButton
            loading={
              isLoading && selectedFlow && selectedFlow === LoginFlow.APPLE
            }
            disabled={disableSocialButtons}
            onAuthorizationResponse={handleAppleResponse}
          />
        )}
      </div>
    </>
  );
};
